import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { GraphQLResponse, ProtoApiService } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { Date as DateModel } from '@lsq-frontend/remote-services/all-java/common-mk0';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { isNil, omitBy } from 'lodash';
import { convertDateToLocalDate } from '../../helpers/date-mapping';
import { mapBuyerPayableSortColumn } from '../../helpers/sort-column-mapping';
import {
    EnumInput,
    InvoiceSummary2,
    PayablesInput,
    PayablesResponse,
    PayablesSortColumn,
    SortDirection
} from '../../model/models';
import { ListInvoicePayLaterEligibleForBuyerRequest } from './request/list-invoice-pay-later-eligible-for-buyer-request';
import { ListInvoicePayLaterEligibleForBuyerResponse } from './response/list-invoice-pay-later-eligible-for-buyer-response';

@Injectable({ providedIn: 'root' })
export class ListInvoicePayLaterEligibleForBuyerService extends ProtoApiService<
    ListInvoicePayLaterEligibleForBuyerRequest,
    ListInvoicePayLaterEligibleForBuyerResponse
> {
    public endpointName: string;
    public requestType = ListInvoicePayLaterEligibleForBuyerRequest;
    public responseType = ListInvoicePayLaterEligibleForBuyerResponse;
    constructor(http: HttpClient, @Inject(SETTINGS) settings: ISettings) {
        super(http, settings);
        this.endpointName = 'v1/query';
        this.serviceName = 'financingGql';
        this.usePascalForUrl = false;
    }

    public requestMapper(req: ListInvoicePayLaterEligibleForBuyerRequest): { query: string } {
        const { skip, take, sortColumn, sortDirection } = req.pagination;
        const statuses = req.buyerInvoiceStatus.map((status) => {
            return new EnumInput(status);
        });
        const input: PayablesInput = omitBy(
            {
                companyFilter: {
                    buyerIds: [req.refBuyerKey],
                    supplierIds: req.sellerKeys ?? []
                },
                payableNameSearch: req.payableNameSearch ?? '',
                dueDateRange: omitBy(req.dueDateRange, isNil),
                statuses: statuses,
                pagination: {
                    skip: skip || 0,
                    take: take || 0,
                    // PAY_LATER_ELIGIBLE should take precedence
                    sortColumn: new EnumType(PayablesSortColumn.PAY_LATER_ELIGIBLE),
                    sortDirection: new EnumType(SortDirection.DESCENDING),
                    secondarySortColumn: new EnumType(mapBuyerPayableSortColumn(sortColumn)),
                    secondarySortDirection: new EnumType(
                        SortDirection[sortDirection] ?? SortDirection.DESCENDING
                    )
                }
            },
            isNil
        );

        const query = jsonToGraphQLQuery({
            payables: {
                __args: { input },
                rows: {
                    id: true,
                    name: true,
                    amount: { cents: true, currencyCode: true },
                    invoiceDateParent: true,
                    originalDueDate: true,
                    estimatedPayLaterAdjustedDueDate: true,
                    dueInDays: true,
                    isGroup: true,
                    termsExtensionDays: true,
                    payLaterEligible: true,
                    relationship: {
                        supplier: { company: { name: true } }
                    }
                },
                total: { payableCount: true }
            }
        });

        return { query: `{${query}}` };
    }

    public responseMapper(
        res: GraphQLResponse<PayablesResponse>
    ): ListInvoicePayLaterEligibleForBuyerResponse {
        const { rows: payables, total } = (res as GraphQLResponse<PayablesResponse>).data.payables;

        const mappedResp = new ListInvoicePayLaterEligibleForBuyerResponse({
            ok: true,
            totalCount: total.payableCount,
            rows: payables.map((payable) => {
                const { supplier } = payable.relationship;

                const invoiceDate = convertDateToLocalDate(payable.invoiceDateParent);
                const invoiceDateNew =
                    invoiceDate &&
                    new DateModel({
                        day: invoiceDate.getDate(),
                        month: invoiceDate.getMonth() + 1,
                        year: invoiceDate.getFullYear()
                    });

                const invoiceDueDate = convertDateToLocalDate(payable.originalDueDate);
                const invoiceDueDateNew =
                    invoiceDueDate &&
                    new DateModel({
                        day: invoiceDueDate.getDate(),
                        month: invoiceDueDate.getMonth() + 1,
                        year: invoiceDueDate.getFullYear()
                    });

                return new InvoiceSummary2({
                    isGroup: payable.isGroup,
                    invoiceDate,
                    invoiceDateNew,
                    invoiceDueDate,
                    invoiceDueDateNew,
                    estimatedPayLaterDueDate: convertDateToLocalDate(
                        payable.estimatedPayLaterAdjustedDueDate
                    ),
                    payLaterEligible: payable.payLaterEligible,
                    refInvoiceKey: payable.id,
                    invoiceNumber: payable.name,
                    dueIn: payable.dueInDays,
                    termsExtensionDays: payable.termsExtensionDays,
                    sellerName: supplier.company.name,
                    invoiceAmount: payable.amount
                });
            })
        });
        return mappedResp;
    }

    public get endpointUrl(): string {
        return super.endpointUrl.replace('/api', '');
    }
}
